import { useState, useEffect, useRef } from 'react';
import { getAccessToken, getSessionId } from './AuthGate.jsx';

// Shared React hooks extracted from deskte-full.jsx. Components in /components
// can import from here so they don't depend on the monolith.

/**
 * State mirrored into localStorage.
 *
 * Note that localStorage is a single ~5MB budget shared by every `dkt6_*` key
 * in the app. One module caching heavy blobs fills it, and from that moment
 * EVERY other key silently stops persisting — which reads as unrelated
 * settings "not saving". So don't mirror attachment bytes through here, and
 * don't swallow the write failure when it happens.
 */
export function useLocalStorage(key, init) {
  const [val, setVal] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : init;
    } catch {
      return init;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(val));
    } catch (err) {
      console.warn(`[useLocalStorage] could not persist "${key}":`, err && err.message);
    }
  }, [key, val]);
  return [val, setVal];
}

/**
 * Open a server-sent events stream to /api/v1/events and dispatch each
 * named event to an optional handler map.
 *
 * Phase 1 (channel-only): the backend only sends `connected` + an SSE
 * heartbeat. We surface `status` so callers can show a "live" indicator
 * during development. Phase 2 will add `client.updated`,
 * `email.received`, `activity.appended` handlers — pass them via the
 * `handlers` map and they'll fire on the matching event names.
 *
 * EventSource cannot set Authorization headers, so the JWT goes on the
 * URL as ?access_token=. The backend verifies it the same way as every
 * other route. Token is fetched once on connect — if it expires
 * mid-stream the server closes the socket and EventSource auto-retries
 * (we re-fetch a fresh token in that path too).
 *
 * Disabled when `enabled` is false (e.g., before login). Returns
 * { status: 'idle' | 'connecting' | 'open' | 'error' } so the caller
 * can render a debug pill without poking at internals.
 */
export function useServerEvents({ enabled = true, handlers = {} } = {}) {
  const [status, setStatus] = useState('idle');
  // Keep the latest handlers in a ref so reconnects don't tear down +
  // rebuild the EventSource every time the parent re-renders.
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  // Tracks the last event id we successfully received, so we can ask
  // the server to replay anything that landed during a brief
  // disconnect. We pass it as `last_event_id` query string on
  // reconnect — EventSource's native Last-Event-ID header doesn't
  // travel through our query-string auth flow reliably.
  const lastEventIdRef = useRef(null);
  // Server-assigned id of the CURRENT stream, echoed back on every ping so
  // the server can refresh exactly this connection's liveness.
  const connIdRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      setStatus('idle');
      return undefined;
    }
    let es = null;
    let cancelled = false;
    let backoffMs = 1000;
    let reconnectTimer = null;
    let watchdog = null;
    // How many times THIS effect's stream has (re)connected. The first
    // `connected` is normal startup; any later one means the stream died and
    // was rebuilt — see the resync dispatch in the `connected` branch below.
    let connectCount = 0;
    const apiBase = import.meta.env.VITE_API_URL || '';

    // If NO frame (named event, generic message, or heartbeat) arrives
    // within this window, treat the stream as silently dead and rebuild
    // it. Some network transitions — laptop sleep/wake, WiFi↔cellular
    // handoff, a proxy killing an idle socket — drop the TCP connection
    // WITHOUT EventSource ever firing `error`, so the tab would sit on a
    // zombie `OPEN` stream showing nothing new until a manual refresh.
    // The server heartbeats every 25s; 65s tolerates two missed beats.
    const STALE_MS = 65000;

    function clearTimers() {
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      if (watchdog) { clearTimeout(watchdog); watchdog = null; }
    }

    // Idempotent reconnect: tear down the current stream (if any) and
    // schedule exactly one fresh connect(). Guarded so a watchdog fire
    // and an `error` event can't both spawn parallel EventSources (which
    // would double every event).
    function scheduleReconnect(delay) {
      if (cancelled || reconnectTimer) return;
      if (watchdog) { clearTimeout(watchdog); watchdog = null; }
      try { if (es) es.close(); } catch {}
      es = null;
      // The old connection is gone; pinging its id would refresh a stream the
      // server no longer has. The next `connected` frame supplies a new one.
      connIdRef.current = null;
      reconnectTimer = setTimeout(() => { reconnectTimer = null; connect(); }, delay);
    }

    // Tell the server we're really here. Fire-and-forget: a failed ping just
    // means we look offline for a while, which errs toward SENDING the Web
    // Push — the safe direction. Never let it reject unhandled.
    async function sendPing() {
      if (cancelled) return;
      try {
        const token = await getAccessToken();
        if (cancelled || !token) return;
        // Con la sesión: sin ella, a quien tiene el autenticador el servidor
        // le contesta 401 (no puede saber si esta sesión ya dio el código).
        const sid = getSessionId();
        await fetch(`${apiBase}/api/v1/sse-ping`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...(sid ? { 'X-Session-Id': sid } : {}),
          },
          body: JSON.stringify({ connId: connIdRef.current }),
          keepalive: true,
        });
      } catch { /* offline / token refresh in flight — next heartbeat retries */ }
    }

    // Any inbound frame proves the stream is alive — push the watchdog out.
    function touch() {
      if (cancelled) return;
      if (watchdog) clearTimeout(watchdog);
      watchdog = setTimeout(() => {
        watchdog = null;
        setStatus('error');
        // Proactive reconnect fetches a fresh token and replays anything
        // missed via Last-Event-ID, so no data is lost.
        scheduleReconnect(500);
      }, STALE_MS);
    }

    async function connect() {
      if (cancelled) return;
      setStatus('connecting');
      let token;
      try {
        token = await getAccessToken();
      } catch (err) {
        // No token = not signed in yet. Retry after a short delay; the
        // hook will tear down via the cleanup below if the user logs out.
        if (!cancelled) scheduleReconnect(2000);
        return;
      }
      if (cancelled) return;
      let url = `${apiBase}/api/v1/events?access_token=${encodeURIComponent(token)}`;
      if (lastEventIdRef.current) {
        url += `&last_event_id=${encodeURIComponent(lastEventIdRef.current)}`;
      }
      try {
        es = new EventSource(url);
      } catch {
        if (!cancelled) scheduleReconnect(backoffMs);
        return;
      }

      es.addEventListener('open', () => {
        if (cancelled) return;
        setStatus('open');
        backoffMs = 1000; // reset on success
        touch();
      });

      // Generic message frames (no `event:` field). EventSource fires
      // this on default `data:`-only frames; we don't emit any in
      // Phase 1 but keep the listener so future code can rely on it.
      es.addEventListener('message', (ev) => {
        touch();
        if (ev.lastEventId) lastEventIdRef.current = ev.lastEventId;
        try {
          const data = JSON.parse(ev.data);
          handlersRef.current.message?.(data);
        } catch {}
      });

      // Wildcard listener through the `error` event AND each named event
      // we expect. EventSource doesn't have a true wildcard, so we
      // hand-register the events Phase 2/3 will care about. Unknown
      // event names are silently ignored on the client side — they
      // still help the network panel show traffic.
      const namedEvents = [
        'connected',
        // Server liveness beat (every 25s). Carries no app payload — its
        // only job is to reset the stale-stream watchdog via touch().
        'heartbeat',
        'client.created',
        'client.updated',
        'client.deleted',
        // Live "who is viewing this client" presence (ephemeral, not replayed).
        'client.presence',
        'policy.created',
        'policy.updated',
        'policy.deleted',
        'policy.renewing_soon',
        'policy.payment.updated',
        'policy.renewal.updated',
        'lead.created',
        'lead.updated',
        'lead.deleted',
        'appointment.created',
        'appointment.updated',
        'appointment.deleted',
        'quote.created',
        'quote.updated',
        'quote.deleted',
        'invoice.created',
        'invoice.updated',
        'invoice.deleted',
        'email.received',
        'email.sent',
        // La bandeja unificada: un mensaje que entra mueve el contador de
        // sin-leer sin recargar. Igual que los comentarios de nota de abajo,
        // TIENE que estar en esta lista: EventSource no tiene comodín.
        'conversation.updated',
        'activity.appended',
        // Phase 4 — entity-level fan-out for tasks / notes / signatures /
        // documents so the SPA reflects mutations across tabs and across
        // teammates without polling.
        'task.created',
        'task.updated',
        'task.deleted',
        'note.created',
        'note.updated',
        'note.deleted',
        // Comentarios de nota. Van aquí obligatoriamente: EventSource no tiene
        // comodín, así que un evento fuera de esta lista simplemente no llega
        // aunque exista su manejador (lección de 2026-07-21, más abajo).
        'note.comment.created',
        'note.comment.updated',
        'note.comment.deleted',
        'signature.created',
        'signature.updated',
        'signature.deleted',
        'document.created',
        'document.updated',
        'document.deleted',
        // Bell — nudges the 🔔 to refetch instantly instead of waiting for
        // its 30s poll (client viewed/accepted/signed something).
        'notification.created',
        // Internal team chat — message fan-out to every connected teammate
        // in the agency, plus channel lifecycle (Phase 2/3).
        'chat.message.created',
        'chat.message.updated',
        'chat.message.deleted',
        'chat.message.reacted',
        'chat.channel.created',
        'chat.channel.archived',
        'chat.typing',
        'chat.presence',
        'chat.read',
        // Platform support tickets. These HAD handlers in the SPA but were
        // missing from this list, and EventSource has no wildcard — so the
        // handlers never ran and the support badge only updated when the user
        // happened to switch views. Adding an event here is NOT optional:
        // this list is the whole subscription (found 2026-07-21).
        'support.ticket.created',
        'support.ticket.updated',
        'support.message.created',
        // Agency tickets (Tareas/Tickets module). Same trap as above: the
        // backend emitted these and the SPA had handlers since #1383, but
        // they were never added HERE, so reassigning a ticket still wasn't
        // visible live (found 2026-07-31).
        'ticket.created',
        'ticket.updated',
        'ticket.deleted',
        // Citas module ('cita.*' ≠ calendar 'appointment.*') + Seguimientos —
        // live since 2026-08-02.
        'cita.created',
        'cita.updated',
        'cita.deleted',
        'followup.created',
        'followup.updated',
        'followup.deleted',
      ];
      for (const name of namedEvents) {
        es.addEventListener(name, (ev) => {
          touch();
          if (ev.lastEventId) lastEventIdRef.current = ev.lastEventId;
          // The server hands us a connection id on `connected`; every
          // `heartbeat` is answered with a ping carrying it back. That round
          // trip is the ONLY proof the stream is really alive — a half-open
          // socket keeps accepting the server's writes for minutes. Without
          // it the server thought we were online and skipped our Web Push,
          // so a dead tab got no SSE AND no push (2026-07-21).
          if (name === 'connected') {
            try { connIdRef.current = JSON.parse(ev.data)?.connId || null; } catch { connIdRef.current = null; }
            sendPing();
            // Resync dispatch (audit 2026-07-31): this interception used to
            // `return` before reaching handlersRef, so the SPA could never
            // learn a stream was REBUILT. Events emitted while we were dark
            // are replayed only from the server's 200-entry ring — a deploy,
            // laptop sleep or replica restart loses everything beyond it,
            // permanently, because every list hydrates exactly once. Tell the
            // app so it can refetch. First connect stays silent (initial
            // hydration is already in flight).
            connectCount += 1;
            if (connectCount > 1) {
              try { handlersRef.current.connected?.({ reconnect: true }); } catch {}
            }
            return;
          }
          if (name === 'heartbeat') { sendPing(); return; }
          try {
            const data = JSON.parse(ev.data);
            handlersRef.current[name]?.(data);
          } catch {}
        });
      }

      es.addEventListener('error', () => {
        if (cancelled) return;
        setStatus('error');
        // EventSource has built-in reconnect; we close + reopen
        // ourselves only to refresh the access token (which expires).
        // Random extra delay: a deploy drops EVERY tab's stream in the same
        // second, and without it they would all knock at once on a server
        // that has just started.
        scheduleReconnect(backoffMs + Math.floor(Math.random() * 4000));
        backoffMs = Math.min(backoffMs * 2, 30000);
      });
    }

    // Network came back (WiFi reconnect, cellular restored): if the stream
    // isn't currently open, reconnect immediately instead of waiting out
    // the watchdog. A healthy stream is left untouched.
    const onOnline = () => {
      if (cancelled) return;
      if (!es || es.readyState === 2 /* CLOSED */) scheduleReconnect(0);
    };
    try { window.addEventListener('online', onOnline); } catch {}

    // Coming back to the tab is the moment that matters most. The watchdog
    // above runs on setTimeout, and browsers heavily throttle (or entirely
    // freeze) timers in a hidden tab or a sleeping laptop — so the stream can
    // be long dead while the watchdog never fires. That is exactly the
    // "nothing arrives until I refresh" the users hit. On every return to the
    // foreground, verify the stream and rebuild it if it isn't OPEN, then ping
    // so presence is accurate immediately.
    const onResume = () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      // readyState: 0 CONNECTING, 1 OPEN, 2 CLOSED.
      if (!es || es.readyState !== 1) { scheduleReconnect(0); return; }
      touch();     // restart the watchdog from now
      sendPing();  // and prove we're alive right away
    };
    try {
      document.addEventListener('visibilitychange', onResume);
      window.addEventListener('focus', onResume);
      // Fired when a page is restored from the back/forward cache, where the
      // socket is already gone but no `error` ever fires.
      window.addEventListener('pageshow', onResume);
    } catch {}

    connect();
    return () => {
      cancelled = true;
      clearTimers();
      try { window.removeEventListener('online', onOnline); } catch {}
      try {
        document.removeEventListener('visibilitychange', onResume);
        window.removeEventListener('focus', onResume);
        window.removeEventListener('pageshow', onResume);
      } catch {}
      if (es) try { es.close(); } catch {}
    };
  }, [enabled]);

  return { status };
}
